import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { BOOK, BONUSES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreOrderDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const totalValue = BONUSES.reduce((s, b) => s + b.value, 0);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        origin_url: window.location.origin,
        customer_name: name.trim(),
        customer_email: email.trim(),
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      setError("Something went wrong starting checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-navy/10" data-testid="preorder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-navy">
            Secure Your Pre-order
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Lock in <span className="font-semibold text-navy">${BOOK.price}</span> and instantly unlock all 3 AI bonus
            guides (${totalValue} value).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="po-name">Full name</Label>
            <Input
              id="po-name"
              data-testid="preorder-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Entrepreneur"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="po-email">Email address</Label>
            <Input
              id="po-email"
              type="email"
              data-testid="preorder-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
            <p className="text-xs text-slate-400">Your bonus guides + receipt are sent here.</p>
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="preorder-error">{error}</p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="preorder-submit-button"
            className="w-full h-12 bg-gold hover:bg-gold-hover text-navy font-bold text-base rounded-xl transition-colors"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to checkout…</>
            ) : (
              <><Lock className="mr-2 h-4 w-4" /> Pre-order for ${BOOK.price}</>
            )}
          </Button>
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure checkout powered by Stripe
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
